<script>
import moment from "moment";
import reminderFrequency from "../components/form/reminderFrequency";

const BANNER_KEY = "experience-banner::contact-details::dismissed";

const STATUS_BADGES = {
  waiting: { label: "Waiting for Review", color: "#D9B116" },
  notApproved: { label: "Not Approved", color: "#EB4654" },
  approved: { label: "Approved", color: "#019256" },
  approvedWithChanges: { label: "Approved With Changes", color: "#019256" },
};

export default {
  name: "ContactDetailsPage",
  components: {
    reminderFrequency,
  },
  props: {
    id: {
      type: [String, Number],
      required: false,
    },
  },
  data() {
    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem(BANNER_KEY) === "1";
    } catch (_e) {
      // localStorage unavailable; keep banner visible
    }
    return {
      user: this.$A.Auth,
      bannerDismissed: dismissed,
      loaded: false,
      approver: { id: 0, name: "", email: "", phone: "", totalProofs: 0 },
      stats: null,
      proofs: [],
      proofsLoaded: false,
      saving: false,
    };
  },
  computed: {
    approverId() {
      const fromRoute = Number(this.id);
      if (fromRoute > 0) return fromRoute;
      const fromView = _.get(this.$A, "viewToViewData.approver.id", 0);
      return Number(fromView) || 0;
    },
    initials() {
      return (this.approver.name || "")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w.charAt(0).toUpperCase())
        .join("");
    },
    addedOn() {
      return this.approver.createdAt
        ? moment.utc(this.approver.createdAt).local().format("MMM D, YYYY")
        : "";
    },
    lastActive() {
      const at = _.get(this.stats, "lastActivityAt", null);
      return at ? moment.utc(at).local().fromNow() : "";
    },
    hasDecisions() {
      return _.get(this.stats, "totalDecisions", 0) > 0;
    },
    statCards() {
      const s = this.stats || {};
      // Plausible-looking numbers shown blurred until there's real data,
      // matching the overview dashboard's empty-stat pattern.
      const blurred = (placeholder) => ({ number: placeholder, blurred: true });
      return [
        {
          key: "awaiting",
          ...(s.proofsAwaitingResponse > 0
            ? { number: String(s.proofsAwaitingResponse) }
            : blurred("3")),
          title: "Awaiting Response",
          subTitle: "Proofs waiting on them right now",
          tooltip:
            "Active proofs on a workflow stage this contact is part of, where they haven't made a decision on the current version yet. Archived proofs aren't counted.",
        },
        {
          key: "response-time",
          ...(this.hasDecisions && s.avgResponseDays != null
            ? { number: this.formatDuration(s.avgResponseDays) }
            : blurred("1.8 days")),
          title: "Avg Response Time",
          subTitle: "From send to decision",
          tooltip:
            "For each version sent to this contact, the time between the send and their decision on it — averaged across every version they've decided.",
        },
        {
          key: "approval-rate",
          ...(this.hasDecisions && s.approvalRatePct != null
            ? {
                number: `${Math.round(s.approvalRatePct)}%`,
                subTitle: `Across ${s.totalDecisions} decision${s.totalDecisions === 1 ? "" : "s"}`,
              }
            : { ...blurred("86%"), subTitle: "Across their decisions" }),
          title: "Approval Rate",
          tooltip:
            "The share of this contact's decisions that were Approved or Approved with Changes. Decisions a user made on their behalf aren't counted.",
        },
        {
          key: "first-round",
          ...(this.hasDecisions && s.firstRoundApprovalPct != null
            ? { number: `${Math.round(s.firstRoundApprovalPct)}%` }
            : blurred("64%")),
          title: "Approved First Round",
          subTitle: "Said yes to the first version",
          tooltip:
            "The share of proofs where this contact's first decision was Approved or Approved with Changes — no new version needed.",
        },
        {
          key: "rounds",
          ...(this.hasDecisions
            ? { number: this.trimNumber(s.avgRoundsOfRevision) }
            : blurred("1.4")),
          title: "Avg Rounds of Revision",
          subTitle: "Versions reviewed per proof",
          tooltip:
            "How many versions of a proof this contact decided on, averaged across the proofs they've responded to. 1 means proofs usually settle on the first version.",
        },
        {
          key: "proofs",
          ...(this.approver.totalProofs > 0
            ? { number: String(this.approver.totalProofs) }
            : blurred("24")),
          title: "Total Proofs",
          subTitle: "Assigned all time",
          tooltip:
            "Every proof this contact has ever been assigned to, including archived proofs. This can be higher than the proofs list, which only shows active proofs.",
        },
        {
          key: "comments",
          number: String(s.commentsLeft || 0),
          title: "Comments Left",
          subTitle: "Annotations and replies",
          tooltip:
            "All annotations and replies this contact has left, across every proof and version.",
        },
        {
          key: "last-activity",
          ...(s.lastActivityAt
            ? {
                number: moment.utc(s.lastActivityAt).local().format("MMM D"),
                subTitle: `Last responded ${this.lastActive}`,
              }
            : { ...blurred("Jun 9"), subTitle: "Their most recent response" }),
          title: "Last Activity",
          tooltip:
            "The most recent time this contact acted on a proof — registering on a review or making a decision.",
        },
      ];
    },
  },
  async mounted() {
    if (!(this.approverId > 0)) {
      this.$A.NavigateToView("/contact-archive", {});
      return;
    }
    await this.loadContact();
    this.loadProofs();
    this.$root.$on("delete::contact::confirmed", this.deleteContact);
  },
  beforeDestroy() {
    this.$root.$off("delete::contact::confirmed", this.deleteContact);
  },
  methods: {
    async loadContact() {
      const payload = await this.$A.ApproverService.GetWithStatsRaw(this.approverId);
      if (!payload || !_.get(payload, "approver.id", 0)) {
        this.$A.NavigateToView("/contact-archive", {});
        return;
      }
      this.approver = payload.approver;
      this.stats = payload;
      this.loaded = true;
      this.$nextTick(() => {
        if (
          this.$refs.reminderFrequency &&
          this.approver.frequency !== undefined &&
          this.approver.timeOfDayToSend !== undefined
        ) {
          this.$refs.reminderFrequency.setData(
            this.approver.timeOfDayToSend,
            this.approver.frequency,
            this.approver.timeZone
          );
        }
      });
    },
    loadProofs() {
      const self = this;
      this.proofs = [];
      this.$A.ProofService.GetProofsByFilterYieldAll(
        { approverId: this.approverId },
        function (results) {
          results.forEach((p) => self.proofs.push(p));
        }
      ).then(() => {
        self.proofsLoaded = true;
      });
    },
    async saveContact() {
      if (this.saving) return;
      if (!this.$A.IsEmailValid(this.approver.email)) {
        this.$A.AlertUser("Please enter a valid email address.");
        return;
      }
      this.saving = true;
      const freq = this.$refs.reminderFrequency;
      this.approver.frequency = freq.frequency;
      this.approver.timeOfDayToSend = freq.time_of_day_to_send;
      this.approver.utcOffsetMin = freq.utcOffsetMin;
      this.approver.timeZone = freq.timeZone;
      const saved = await this.$A.ApproverService.Save(this.approver);
      if (saved && saved.id > 0) {
        this.approver.name = saved.name;
      }
      this.saving = false;
    },
    async deleteContact() {
      if (this.approver.id > 0) {
        await this.$A.ApproverService.DeleteSingle(this.approver.id);
        const self = this;
        setTimeout(function () {
          self.$A.NavigateToView("/contact-archive", {});
        }, 1000);
      }
    },
    dismissBanner() {
      this.bannerDismissed = true;
      try {
        window.localStorage.setItem(BANNER_KEY, "1");
      } catch (_e) {
        // ignore
      }
    },
    switchToClassic() {
      this.$A.NavigateToView("/approver-item", { approver: this.approver });
    },
    viewProof(proof) {
      this.$A.NavigateToView("/proof-details", { proof: proof });
    },
    proofBadge(proof) {
      if (proof.status === 5) return STATUS_BADGES.approved;
      if (proof.status === 6) return STATUS_BADGES.approvedWithChanges;
      if (proof.status === 4) return STATUS_BADGES.notApproved;
      return STATUS_BADGES.waiting;
    },
    formatDuration(days) {
      const hours = days * 24;
      if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`;
      if (hours < 48) return `${this.trimNumber(hours)} hrs`;
      return `${this.trimNumber(days)} days`;
    },
    trimNumber(n) {
      if (n == null) return "—";
      const rounded = Math.round(n * 10) / 10;
      return String(rounded % 1 === 0 ? Math.round(rounded) : rounded.toFixed(1));
    },
    formatDate(date) {
      return date ? moment.utc(date).local().format("MMM D, YYYY") : "";
    },
  },
};
</script>

<template>
  <div class="nav-margin contact-details-page">
    <navigation />

    <div class="page-body">
      <div class="experience-switch" v-if="!bannerDismissed && approver.id > 0">
        <div class="experience-msg">
          <span class="experience-pill">New</span>
          <span class="experience-text">You're using the new contact details.</span>
        </div>
        <div class="experience-actions">
          <button
            type="button"
            class="experience-link experience-link-btn"
            v-b-modal="'feedbackModal-newContactDetails'"
          >Give feedback</button>
          <span class="experience-divider" aria-hidden="true">·</span>
          <button
            type="button"
            class="experience-link experience-link-btn"
            @click="switchToClassic"
          >Switch to classic →</button>
        </div>
        <button
          type="button"
          class="experience-close"
          @click="dismissBanner"
          aria-label="Dismiss banner"
          title="Dismiss"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <feedbackModal
        name="feedbackModal-newContactDetails"
        campaign-key="new-contact-details-v1"
        title="How's the new contact page?"
        subtitle="Your take helps us decide what to keep."
        scale-question="Compared to the classic experience, how would you rate this?"
        :scale-min="1"
        :scale-max="7"
        scale-low-label="Much worse"
        scale-high-label="Much better"
        text-prompt="What's working or not working?"
        text-placeholder="Tell us anything that helps."
        submit-label="Send feedback"
      />

      <div class="contact-shell">
        <div class="contact-inner" :class="{ 'is-loading': !loaded }">
          <!-- Header -->
          <div class="contact-head">
            <div class="contact-identity">
              <div class="contact-avatar" aria-hidden="true">{{ initials }}</div>
              <div class="contact-titles">
                <div class="contact-eyebrow">Contact</div>
                <h1 class="contact-name">{{ approver.name }}</h1>
                <div class="contact-meta">
                  <a class="contact-email" :href="`mailto:${approver.email}`">{{ approver.email }}</a>
                  <span v-if="approver.phone" class="meta-divider" aria-hidden="true">·</span>
                  <a v-if="approver.phone" class="contact-email" :href="`tel:${approver.phone}`">{{ approver.phone }}</a>
                  <span v-if="addedOn" class="meta-divider" aria-hidden="true">·</span>
                  <span v-if="addedOn">Added {{ addedOn }}</span>
                  <span v-if="lastActive" class="meta-divider" aria-hidden="true">·</span>
                  <span v-if="lastActive">Last active {{ lastActive }}</span>
                </div>
              </div>
            </div>
            <div class="contact-head-actions">
              <button
                type="button"
                class="delete-contact-btn"
                v-b-modal.deleteContactModal
                v-show="approver.id > 0"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                Delete contact
              </button>
            </div>
          </div>

          <!-- Stat tiles -->
          <div class="stat-grid">
            <div class="stat-card" v-for="card in statCards" :key="card.key">
              <div class="stat-number" :class="{ 'is-blurred': card.blurred }">{{ card.number }}</div>
              <div class="stat-title">
                {{ card.title }}
                <span
                  class="stat-info"
                  tabindex="0"
                  aria-label="How this is calculated"
                  v-b-tooltip.hover.top="card.tooltip"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                </span>
              </div>
              <div class="stat-subtitle">{{ card.subTitle }}</div>
            </div>
          </div>

          <!-- Info + proofs -->
          <div class="contact-columns">
            <div class="panel-card info-card">
              <div class="card-head">Contact Information</div>
              <div class="info-card-body">
                <div class="form-field">
                  <label class="field-label" for="contact-name-input">Name</label>
                  <input
                    id="contact-name-input"
                    name="name"
                    type="text"
                    placeholder="Contact name"
                    class="field-input"
                    v-model="approver.name"
                    data-cy="contactName"
                  />
                </div>
                <div class="form-field">
                  <label class="field-label" for="contact-email-input">Email</label>
                  <input
                    id="contact-email-input"
                    name="email"
                    type="email"
                    placeholder="Email"
                    class="field-input"
                    :class="{ 'has-error': approver.email && !$A.IsEmailValid(approver.email) }"
                    v-model="approver.email"
                    data-cy="approverEmail"
                  />
                </div>
                <div class="form-field">
                  <label class="field-label" for="contact-phone-input">Phone <span class="field-optional">(optional)</span></label>
                  <input
                    id="contact-phone-input"
                    name="phone"
                    type="tel"
                    placeholder="Phone number"
                    class="field-input"
                    v-model="approver.phone"
                    data-cy="contactPhone"
                  />
                </div>
                <div class="reminder-divider">
                  <span class="reminder-label">Automated Reminders</span>
                </div>
                <reminder-frequency ref="reminderFrequency" />
                <div class="info-card-actions">
                  <button
                    type="button"
                    class="save-btn"
                    :disabled="saving"
                    @click="saveContact"
                    data-cy="saveApproverBtn"
                  >
                    {{ saving ? "Saving…" : "Save changes" }}
                  </button>
                </div>
              </div>
            </div>

            <div class="panel-card proofs-card">
              <div class="card-head">
                Proofs
                <span class="count-pill" v-if="proofsLoaded">{{ proofs.length }}</span>
              </div>
              <div class="proof-rows">
                <div class="proof-row" v-for="proof in proofs" :key="proof.id">
                  <div class="proof-main">
                    <a class="proof-name" href="#" @click.prevent="viewProof(proof)">{{ proof.name }}</a>
                    <div class="proof-sub">
                      <span class="status-badge">
                        <span class="status-dot" :style="{ background: proofBadge(proof).color }"></span>
                        {{ proofBadge(proof).label }}
                      </span>
                      <span class="version-text">
                        Version {{ proof.currentVersion }}<template v-if="proof.currentVersionCreatedAt"> · Created {{ formatDate(proof.currentVersionCreatedAt) }}</template>
                      </span>
                    </div>
                  </div>
                  <a class="view-link" href="#" @click.prevent="viewProof(proof)">View</a>
                </div>

                <div v-if="proofsLoaded && !proofs.length" class="list-empty">
                  <div class="empty-icon">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                  </div>
                  <div class="empty-title">No proofs yet</div>
                  <div class="empty-text">
                    Proofs will show up here as soon as {{ approver.name || "this contact" }} is added to a workflow.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <modal
      name="deleteContactModal"
      eventToEmitOnConfirmation="delete::contact::confirmed"
      question="Delete This Contact?"
      body="Are you sure you want to delete this contact? You cannot undo this action."
      confirm-button-text="Yes, Delete"
    />
  </div>
</template>

<style scoped>
.page-body {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 64px);
  background: #f4f5f7;
}

/* ----- experience banner ----- */
.experience-switch {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  column-gap: 16px;
  row-gap: 4px;
  padding: 8px 48px;
  background: rgba(1, 146, 86, 0.06);
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  font-size: 13px;
  color: #4b5563;
  flex-shrink: 0;
}
.experience-msg {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.experience-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.experience-pill {
  display: inline-flex;
  padding: 2px 8px;
  background: #019256;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  letter-spacing: 0.02em;
}
.experience-link {
  color: #019256;
  font-weight: 600;
  text-decoration: none;
}
.experience-link:hover {
  text-decoration: underline;
  color: #017a48;
}
.experience-link-btn {
  background: transparent;
  border: 0;
  padding: 0;
  font: inherit;
  font-weight: 600;
  color: #019256;
  cursor: pointer;
}
.experience-divider {
  color: #c2c8d0;
  font-weight: 600;
}
.experience-close {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.experience-close:hover {
  background: rgba(15, 23, 42, 0.06);
  color: #111827;
}

/* ----- shell ----- */
.contact-shell {
  flex: 1;
}
.contact-inner {
  max-width: 1320px;
  margin: 0 auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  transition: opacity 150ms ease;
}
.contact-inner.is-loading {
  opacity: 0.45;
  pointer-events: none;
}

/* ----- header ----- */
.contact-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.contact-identity {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}
.contact-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 999px;
  background: rgba(1, 146, 86, 0.1);
  border: 1px solid rgba(1, 146, 86, 0.18);
  color: #017a48;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.02em;
  flex-shrink: 0;
}
.contact-titles {
  min-width: 0;
}
.contact-eyebrow {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 4px;
}
.contact-name {
  font-size: 28px;
  line-height: 34px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.contact-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
}
.contact-email {
  color: #019256;
  font-weight: 600;
  text-decoration: none;
}
.contact-email:hover {
  color: #017a48;
  text-decoration: underline;
}
.meta-divider {
  color: #c2c8d0;
  font-weight: 600;
}
.delete-contact-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px;
  background: #fff;
  border: 1px solid rgba(235, 70, 84, 0.3);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #d23a47;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease;
}
.delete-contact-btn:hover {
  background: rgba(235, 70, 84, 0.06);
  border-color: rgba(235, 70, 84, 0.5);
}

/* ----- stat tiles ----- */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}
@media (max-width: 1280px) {
  .stat-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
@media (max-width: 991px) {
  .stat-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 575px) {
  .stat-grid {
    grid-template-columns: 1fr;
  }
}
.stat-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 8px 24px -16px rgba(8, 11, 13, 0.1);
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
}
.stat-number {
  font-size: 32px;
  line-height: 38px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.01em;
}
.stat-number.is-blurred {
  filter: blur(9px);
  user-select: none;
  pointer-events: none;
}
.stat-title {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
}
.stat-info {
  display: inline-flex;
  align-items: center;
  color: #9ca3af;
  cursor: help;
  transition: color 120ms ease;
}
.stat-info:hover,
.stat-info:focus {
  color: #4b5563;
  outline: none;
}
.stat-info svg {
  display: block;
}
.stat-subtitle {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}

/* ----- columns ----- */
.contact-columns {
  display: grid;
  grid-template-columns: minmax(360px, 5fr) 7fr;
  gap: 16px;
  align-items: start;
}
@media (max-width: 1118px) {
  .contact-columns {
    grid-template-columns: 1fr;
  }
}
.panel-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 8px 24px -16px rgba(8, 11, 13, 0.1);
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.card-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 20px;
  background: #fbfbfc;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px 12px 0 0;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  flex-shrink: 0;
}
.count-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  padding: 1px 7px;
  background: rgba(1, 146, 86, 0.1);
  color: #017a48;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

/* ----- info form ----- */
.info-card-body {
  padding: 20px;
}
.form-field {
  margin-bottom: 14px;
}
.field-label {
  display: block;
  margin-bottom: 5px;
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
}
.field-optional {
  color: #9aa0a6;
  font-weight: 400;
}
.field-input {
  display: block;
  width: 100%;
  padding: 9px 13px;
  height: 38px;
  background: #fff;
  border: 1px solid #d3d6db;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.05);
  border-radius: 8px;
  font-size: 14px;
  color: #111827;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
.field-input:focus {
  outline: none;
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}
.field-input.has-error {
  border-color: #eb4654;
}
.field-input.has-error:focus {
  box-shadow: 0 0 0 3px rgba(235, 70, 84, 0.12);
}
.reminder-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0 14px;
}
.reminder-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: rgba(15, 23, 42, 0.08);
}
.reminder-label {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
}
/* The shared reminderFrequency component lays out as two side-by-side
   col-md-6 columns; stack them full-width inside this narrow card. */
.info-card-body ::v-deep .col-md-12 {
  padding: 0;
}
.info-card-body ::v-deep .col-md-6 {
  flex: 0 0 100%;
  max-width: 100%;
  padding: 0;
}
.info-card-body ::v-deep .label-text {
  margin-left: 0 !important;
}
.info-card-body ::v-deep .weekDays-selector {
  padding-left: 0 !important;
}
.info-card-body ::v-deep .contact-input {
  margin-left: 0 !important;
}
.info-card-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}
.save-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 9px 20px;
  background: #019256;
  border: 0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  transition: background-color 120ms ease;
}
.save-btn:hover {
  background: #017a48;
}
.save-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

/* ----- proofs list ----- */
.proof-rows {
  max-height: 560px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}
.proof-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 13px 20px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  transition: background-color 120ms ease;
}
.proof-row:hover {
  background: #f9f9fa;
}
.proof-row:last-child {
  border-bottom: 0;
}
.proof-main {
  flex: 1;
  min-width: 0;
}
.proof-name {
  display: inline-block;
  max-width: 100%;
  font-size: 14px;
  font-weight: 400;
  color: #0f172a;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: color 120ms ease;
}
.proof-name:hover {
  color: #019256;
  text-decoration: none;
}
.proof-sub {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 12px;
  color: #6b7280;
  margin-top: 3px;
}
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 11px;
  font-weight: 600;
}
.status-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 999px;
  flex-shrink: 0;
}
.version-text {
  font-size: 12px;
}
.view-link {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #019256;
  background: transparent;
  border: 1px solid rgba(1, 146, 86, 0.25);
  border-radius: 6px;
  text-decoration: none;
  flex-shrink: 0;
  transition: background-color 120ms ease, border-color 120ms ease;
}
.view-link:hover {
  background: rgba(1, 146, 86, 0.06);
  border-color: rgba(1, 146, 86, 0.45);
  color: #017a48;
  text-decoration: none;
}
.list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 32px;
  min-height: 240px;
}
.empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 999px;
  margin-bottom: 14px;
  background: rgba(1, 146, 86, 0.1);
  color: #019256;
}
.empty-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.01em;
}
.empty-text {
  margin-top: 5px;
  font-size: 12.5px;
  font-weight: 500;
  color: #6b7280;
  line-height: 1.55;
  max-width: 320px;
}

@media (max-width: 575px) {
  .contact-inner {
    padding: 16px;
    gap: 16px;
  }
  .contact-name {
    font-size: 22px;
    line-height: 28px;
  }
  .proof-row {
    flex-wrap: wrap;
    gap: 10px;
    padding: 12px 16px;
  }
  .view-link {
    margin-left: auto;
  }
}
</style>
